import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";

export interface ScenarioCoordinate {
  lat: number;
  lng: number;
}

export interface ScenarioConfig {
  coordinate?: ScenarioCoordinate;
  /** User-supplied display name. Falls back to the backend `scenarioName` when unset. */
  friendlyName?: string;
}

/**
 * Browser-local persistence of per-scenario UI config (pin coordinates +
 * friendly name) for the Traffic Flow tab. Keyed by tenant so a user with
 * access to multiple tenants doesn't see another tenant's settings.
 *
 * NOT synced to DDB — DDB-backed persistence is a follow-up plan once the
 * UX is validated.
 */
interface ScenarioCoordinatesState {
  /** `{ [tenantId]: { [scenarioUuid]: ScenarioConfig } }` */
  byTenant: Record<string, Record<string, ScenarioConfig>>;
  setCoordinate: (tenantId: string, scenarioUuid: string, coord: ScenarioCoordinate) => void;
  removeCoordinate: (tenantId: string, scenarioUuid: string) => void;
  setFriendlyName: (tenantId: string, scenarioUuid: string, friendlyName: string | undefined) => void;
  /** Returns the per-scenario config map for a tenant, or `{}` if none stored. */
  getForTenant: (tenantId: string) => Record<string, ScenarioConfig>;
}

function patchConfig(
  state: { byTenant: Record<string, Record<string, ScenarioConfig>> },
  tenantId: string,
  scenarioUuid: string,
  patch: Partial<ScenarioConfig>,
) {
  const tenant = state.byTenant[tenantId] ?? {};
  const prev = tenant[scenarioUuid] ?? {};
  const next: ScenarioConfig = { ...prev, ...patch };
  // Drop empty entries so the store doesn't accumulate orphan keys
  if (next.coordinate === undefined && (next.friendlyName === undefined || next.friendlyName === "")) {
    const { [scenarioUuid]: _removed, ...rest } = tenant;
    return { byTenant: { ...state.byTenant, [tenantId]: rest } };
  }
  return { byTenant: { ...state.byTenant, [tenantId]: { ...tenant, [scenarioUuid]: next } } };
}

/**
 * Stable empty-object reference returned when a tenant has no stored config.
 * Hoisted so selectors that fall back to "no config" return the same reference
 * on every render — otherwise Zustand sees a fresh `{}` from inline `?? {}`
 * and triggers an infinite re-render loop via useSyncExternalStore.
 *
 * Use this from selectors instead of `?? {}`:
 *   useScenarioCoordinatesStore((s) => s.byTenant[tenantId] ?? EMPTY_SCENARIO_CONFIG);
 */
export const EMPTY_SCENARIO_CONFIG: Record<string, ScenarioConfig> = Object.freeze({}) as Record<
  string,
  ScenarioConfig
>;

export const useScenarioCoordinatesStore = create<ScenarioCoordinatesState>()(
  persist(
    (set, get) => ({
      byTenant: {},
      setCoordinate: (tenantId, scenarioUuid, coord) =>
        set((state) => patchConfig(state, tenantId, scenarioUuid, { coordinate: coord })),
      removeCoordinate: (tenantId, scenarioUuid) =>
        set((state) => patchConfig(state, tenantId, scenarioUuid, { coordinate: undefined })),
      setFriendlyName: (tenantId, scenarioUuid, friendlyName) =>
        set((state) =>
          patchConfig(state, tenantId, scenarioUuid, {
            friendlyName: friendlyName && friendlyName.trim() !== "" ? friendlyName.trim() : undefined,
          }),
        ),
      getForTenant: (tenantId) => get().byTenant[tenantId] ?? {},
    }),
    {
      name: "traffic-flow:scenario-coordinates",
      storage: createJSONStorage(() => AsyncStorage),
      version: 2,
      // v1 stored `{[uuid]: {lat,lng}}` directly. v2 wraps as `{coordinate: {lat,lng}}`.
      migrate: (persisted: unknown, version: number) => {
        if (version >= 2 || !persisted || typeof persisted !== "object") return persisted as never;
        const root = persisted as { byTenant?: Record<string, Record<string, unknown>> };
        if (!root.byTenant) return persisted as never;
        const upgraded: Record<string, Record<string, ScenarioConfig>> = {};
        for (const [tenantId, tenantMap] of Object.entries(root.byTenant)) {
          upgraded[tenantId] = {};
          for (const [uuid, raw] of Object.entries(tenantMap)) {
            if (raw && typeof raw === "object" && "lat" in raw && "lng" in raw) {
              const r = raw as { lat: number; lng: number };
              upgraded[tenantId][uuid] = { coordinate: { lat: r.lat, lng: r.lng } };
            }
          }
        }
        return { byTenant: upgraded };
      },
    },
  ),
);
